/**
 * Ask the server where one note's processing is (#14360).
 *
 * The one client-side entry point to `GET /api/notes/[id]/processing-status`:
 * the note page's `useNoteProcessingStatus` polls it. It takes nothing but a
 * `noteId` so the recording bar can call it too, for the "Notes ready" state
 * it does not have yet.
 *
 * Total: every failure — a refusal (the route's one 404), a server error, a
 * network drop, an abort, a body this code does not recognise — is `null`.
 * A caller that polls keeps its last known stage and asks again; none of
 * them can act on WHICH failure it was, and the route deliberately does not
 * say.
 */

import type { MeetingTranscriptionStatus } from '@/lib/meetings/transcription-status'
import {
  NOTE_PROCESSING_STEPS,
  type NoteProcessingStage,
  type NoteProcessingStatus,
} from '@/lib/notes/note-processing'
import { noteProcessingStatusApiPath } from '@/lib/notes/note-paths'

/**
 * Every stage the route can answer with. Keyed by the union so a stage added
 * to `NoteProcessingStage` fails to compile here until it is listed — the
 * list once missed `PASS_FAILED` (#14362), which read a settled note as a
 * failed request and left every poller asking again until its ceiling.
 */
const SETTLED_STAGES: Record<
  Exclude<NoteProcessingStage, (typeof NOTE_PROCESSING_STEPS)[number]>,
  true
> = { DONE: true, PASS_FAILED: true, FAILED: true, NONE: true }

const KNOWN_STAGES: ReadonlySet<string> = new Set<string>([
  ...NOTE_PROCESSING_STEPS,
  ...Object.keys(SETTLED_STAGES),
])

function isTranscriptionStatus(
  value: unknown,
): value is MeetingTranscriptionStatus {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  const nullableNumber = (field: unknown) =>
    field === null || typeof field === 'number'
  return (
    typeof candidate.status === 'string' &&
    nullableNumber(candidate.segmentsCommitted) &&
    typeof candidate.attemptCount === 'number' &&
    (candidate.lastError === null || typeof candidate.lastError === 'string') &&
    nullableNumber(candidate.elapsedMs)
  )
}

function isNoteProcessingStatus(value: unknown): value is NoteProcessingStatus {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.stage === 'string' &&
    KNOWN_STAGES.has(candidate.stage) &&
    typeof candidate.hasTranscript === 'boolean' &&
    (candidate.stageChangedAt === null ||
      typeof candidate.stageChangedAt === 'string') &&
    (candidate.lateAudioPending === undefined ||
      typeof candidate.lateAudioPending === 'boolean') &&
    (candidate.transcription === null ||
      isTranscriptionStatus(candidate.transcription))
  )
}

export async function fetchNoteProcessingStatus(
  noteId: string,
  init: { signal?: AbortSignal } = {},
): Promise<NoteProcessingStatus | null> {
  try {
    const response = await fetch(noteProcessingStatusApiPath(noteId), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: init.signal,
    })
    if (!response.ok) return null
    const body: unknown = await response.json()
    return isNoteProcessingStatus(body) ? body : null
  } catch {
    return null
  }
}
